import {
  getListingCurrency,
  GetListingCurrencyParams,
  getListingPriceToDisplay,
  GetListingPriceToDisplayParams,
} from '@util/listings';
import { reaction } from 'mobx';
import { MlsComplianceData } from 'mobx/stores/app-store';
import { useStores } from 'mobx/useStores';
import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { PropertyTypeEnum } from '@engelvoelkersamericas/api';

type GetListingPriceParams = Omit<GetListingCurrencyParams, 'mlsComplianceData'> &
  GetListingPriceToDisplayParams & {
    custom14?: string;
    custom15?: string;
  };

type GetListingPriceReturn = {
  rawPrice: number;
  formattedPrice: string;
  originalCurrency: string;
  displayedCurrency: string;
};

interface CurrencyHook {
  convertCurrency: (val: number, baseCurrency: string, targetCurrency?: string) => number;
  formatCurrency: (
    val: number,
    currency: string,
    currencyDisplay?: Intl.NumberFormatOptions['currencyDisplay'],
  ) => string;
  getListingPrice: (params: GetListingPriceParams) => GetListingPriceReturn;
  exchangeRates: Record<string, number> | null;
  preferredCurrency: string | null;
}

export const useCurrency = (): CurrencyHook => {
  const { appStore } = useStores();
  const t = useTranslations();
  const [preferredCurrency, setPreferredCurrency] = useState<string | null>(
    appStore.preferredCurrency,
  );
  const [exchangeRates, setExchangeRates] = useState<Record<string, number> | null>(
    appStore.exchangeRates,
  );
  const [mlsComplianceData, setMlsComplianceData] = useState<MlsComplianceData | null>(
    appStore.mlsComplianceData,
  );

  // Listen for changes in the store here because we can't use the observer HOC in a hook
  useEffect(() => {
    const disposers: (() => void)[] = [];

    disposers.push(
      reaction(
        () => appStore.preferredCurrency,
        (val) => setPreferredCurrency(val),
      ),
    );
    disposers.push(
      reaction(
        () => appStore.exchangeRates,
        (val) => setExchangeRates(val),
      ),
    );
    disposers.push(
      reaction(
        () => appStore.mlsComplianceData,
        (val) => setMlsComplianceData(val),
      ),
    );

    return () => {
      disposers.forEach((disposer) => disposer());
    };
  }, []);

  /** Returns the exchange rate between two currencies */
  const getExchangeRate = useCallback(
    (baseCurrency: string, targetCurrency: string) => {
      if (!exchangeRates || !exchangeRates[targetCurrency] || !exchangeRates[baseCurrency]) {
        return 1;
      }

      return exchangeRates[targetCurrency] / exchangeRates[baseCurrency];
    },
    [exchangeRates],
  );

  /** Converts a value to another currency */
  const convertCurrency = useCallback(
    (val: number, baseCurrency: string, targetCurrency?: string) => {
      const target = targetCurrency ?? preferredCurrency ?? baseCurrency;
      if (target === baseCurrency) {
        return val;
      }

      const rate = getExchangeRate(baseCurrency, target);
      return val * rate;
    },
    [getExchangeRate, preferredCurrency, mlsComplianceData],
  );

  const formatCurrency = useCallback(
    (
      val: number,
      currency: string,
      currencyDisplay: Intl.NumberFormatOptions['currencyDisplay'] = 'narrowSymbol',
    ) => {
      let formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        currencyDisplay: currencyDisplay,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(val);

      formatted = formatted.replace('CA$', 'CAD $');
      formatted = formatted.replace('MX$', 'MXN $');

      return formatted;
    },
    [],
  );

  /** Returns the price that should be displayed after accounting for the user's preferred currency */
  const getListingPrice = useCallback(
    ({
      country,
      listingCurrency,
      originatingSystemName,
      standardStatus,
      listPrice,
      closePrice,
      closePriceToDisplay,
      custom14,
      custom15,
      leaseAmount,
      leaseAmountFrequency,
      propertyType,
    }: GetListingPriceParams): GetListingPriceReturn => {
      // Get the currency of the listing
      const originalCurrency = getListingCurrency({
        originatingSystemName,
        country,
        listingCurrency,
        mlsComplianceData,
      });

      // Convert the price to the user's preferred currency
      const priceToDisplay = getListingPriceToDisplay({
        standardStatus: standardStatus,
        originatingSystemName: originatingSystemName,
        listPrice: listPrice,
        closePrice: closePrice,
        closePriceToDisplay: closePriceToDisplay,
        propertyType: propertyType,
        leaseAmount: leaseAmount,
      });

      const finalCurrency = preferredCurrency ?? originalCurrency;
      const convertedPrice = convertCurrency(priceToDisplay, originalCurrency, finalCurrency);
      const priceSuffix = originatingSystemName == 'CENTRIS' && (custom14 || custom15) ? '+tx' : '';
      const formattedPrice = formatCurrency(convertedPrice, finalCurrency);
      const translateLeaseAmountFrequency =
        propertyType && PropertyTypeEnum.ResidentialLease === propertyType
          ? ` ${t(leaseAmountFrequency as any)}`
          : '';
      return {
        rawPrice: priceToDisplay,
        formattedPrice: `${finalCurrency} ${formattedPrice}${priceSuffix}${translateLeaseAmountFrequency}`,
        originalCurrency: originalCurrency,
        displayedCurrency: finalCurrency,
      };
    },
    [mlsComplianceData, preferredCurrency, formatCurrency, convertCurrency],
  );

  return { exchangeRates, preferredCurrency, convertCurrency, formatCurrency, getListingPrice };
};
